<template>
    <section class="sponsor-list" aria-label="Sponsor list">
        <div class="datatable-toolbar sponsor-toolbar">
            <div class="sponsor-search">
                <label class="sr-only" for="sponsor-search">Search sponsors</label>
                <input id="sponsor-search" type="search" class="form-control" placeholder="Search sponsors" v-model="dataFilter.searchText" @keyup.enter="doFind">
                <button class="btn btn-outline-primary" type="button" @click="doFind">Search</button>
                <button v-if="dataFilter.searchText" class="btn btn-link text-secondary clear-search" type="button" @click="clearSearch">Clear</button>
            </div>

            <RouterLink to="/sponsor/add" class="btn btn-primary add-sponsor-button">
                <i class="pi pi-plus mr-2" aria-hidden="true"></i>
                Add sponsor
            </RouterLink>
        </div>

        <div v-if="isLoading || dataList.length" class="table-responsive table-container sponsor-table-wrapper">
            <table class="table table-striped sponsor-table">
                <thead>
                <tr>
                    <th scope="col">No</th>
                    <th scope="col">Name</th>
                    <th scope="col">Website</th>
                    <th scope="col">Category</th>
                    <th scope="col">Status</th>
                    <th scope="col">Banner</th>
                    <th scope="col">Actions</th>
                </tr>
                </thead>

                <tbody>
                <tr v-if="isLoading">
                    <td colspan="7" class="table-message">
                        <span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                        Loading sponsors…
                    </td>
                </tr>
                <tr v-else v-for="(data, index) in dataList" :key="data.id">
                    <td class="order-number">{{ index + 1 }}</td>
                    <td class="text-left sponsor-name">{{ data.name }}</td>
                    <td class="text-left sponsor-website">
                        <a v-if="getWebsiteUrl(data.url)" :href="getWebsiteUrl(data.url)" target="_blank" rel="noopener noreferrer">{{ data.url }}</a>
                        <span v-else class="text-muted">—</span>
                    </td>
                    <td class="text-left">{{ data.categoryLabel || '—' }}</td>
                    <td>
                        <span class="badge badge-success" v-if="data.status === 1">{{ data.statusLabel }}</span>
                        <span class="badge badge-dark" v-else>{{ data.statusLabel }}</span>
                    </td>
                    <td class="banner-cell">
                        <Image v-if="data.image" :src="getImageUrl(data.image)" :alt="`Banner for ${data.name}`" width="72" preview />
                        <span v-else class="text-muted">—</span>
                    </td>
                    <td class="actions">
                        <button type="button" class="btn btn-outline-secondary" :aria-label="`Edit ${data.name}`" v-tooltip="'Edit sponsor'" @click="onEdit(data)"><i class="pi pi-pencil" aria-hidden="true"></i></button>
                        <button type="button" class="btn btn-outline-danger" :aria-label="`Remove ${data.name}`" v-tooltip="'Remove sponsor'" @click="onRemove(data)"><i class="pi pi-trash" aria-hidden="true"></i></button>
                    </td>
                </tr>
                </tbody>
            </table>
        </div>

        <div v-else class="sponsor-empty-state" role="status">
            <i class="pi pi-star" aria-hidden="true"></i>
            <h2>{{ dataFilter.searchText ? 'No sponsors match your search.' : 'No sponsors yet.' }}</h2>
            <p>{{ dataFilter.searchText ? 'Try a different keyword or clear the search.' : 'Add a sponsor to start building your partner directory.' }}</p>
            <button v-if="dataFilter.searchText" class="btn btn-outline-primary" type="button" @click="clearSearch">Clear search</button>
            <RouterLink v-else to="/sponsor/add" class="btn btn-primary">
                <i class="pi pi-plus mr-2" aria-hidden="true"></i>
                Add sponsor
            </RouterLink>
        </div>

        <ConfirmDialog></ConfirmDialog>
    </section>
</template>

<script>
import {defineComponent} from 'vue'
import ConfirmDialog from "primevue/confirmdialog";
import apiService from "@/services/ApiService";
import Image from "primevue/image";

export default defineComponent({
    name: "SponsorList",
    components: {
        ConfirmDialog, Image
    },
    data() {
        return {
            dataList: [],
            isLoading: true,
            dataFilter: {
                searchText: ''
            }
        }
    },
    methods: {
        onEdit(data) {
            this.$router.push(`/sponsor/edit/${data.id}`);
        },
        onRemove(data) {
            this.$confirm.require({
                message: `Remove ${data.name} from the sponsor directory?`,
                header: 'Remove sponsor',
                icon: 'pi pi-exclamation-triangle',
                acceptClass: 'p-button-danger',
                accept: () => this.doRemove(data.id)
            });
        },
        doFind() {
            const params = {
                SearchText: this.dataFilter.searchText
            };
            this.isLoading = true;
            apiService.get('/sponsor', {params: params})
                .then(response => {
                    this.dataList = response.data || [];
                }).catch(error => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Failed to get sponsors',
                    detail: error.response?.data?.Message || 'Please try again.',
                    life: 5000
                });
            }).finally(() => {
                this.isLoading = false;
            });
        },
        clearSearch() {
            this.dataFilter.searchText = '';
            this.doFind();
        },
        doRemove(id) {
            apiService.delete(`/sponsor/${id}`)
                .then(() => {
                    this.doFind();
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Sponsor has been removed',
                        life: 5000
                    });
                }).catch(error => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Failed to remove sponsor',
                    detail: error.response?.data?.Message || 'Please try again.',
                    life: 5000
                });
            });
        },
        getImageUrl(image){
            return `${apiService.defaults.baseURL}/file/${image}`;
        },
        getWebsiteUrl(url) {
            if (!url) {
                return '';
            }
            const normalizedUrl = /^https?:\/\//i.test(url) ? url : `https://${url}`;
            try {
                const parsedUrl = new URL(normalizedUrl);
                return ['http:', 'https:'].includes(parsedUrl.protocol) ? normalizedUrl : '';
            } catch (_) {
                return '';
            }
        }
    },
    mounted() {
        this.doFind();
    }
})
</script>

<style scoped>
.sponsor-toolbar {
    align-items: center;
    display: flex;
    gap: 0.75rem;
    justify-content: space-between;
    margin-bottom: 0.5rem;
}

.sponsor-search {
    align-items: center;
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.sponsor-search .form-control {
    max-width: 19rem;
}

.clear-search {
    font-size: 0.875rem;
    padding-left: 0.25rem;
    padding-right: 0.25rem;
}

.add-sponsor-button {
    white-space: nowrap;
}

.sponsor-table-wrapper {
    margin: 0;
}

.sponsor-table th {
    white-space: nowrap;
}

.sponsor-name {
    font-weight: 600;
    min-width: 12rem;
}

.sponsor-website {
    min-width: 13rem;
    overflow-wrap: anywhere;
}

.banner-cell {
    min-width: 7rem;
    text-align: center;
}

.table-message {
    color: #6c757d;
    padding: 2.5rem 1rem !important;
    text-align: center;
}

.sponsor-empty-state {
    align-items: center;
    border: 1px solid #e2e6ec;
    border-radius: 0.65rem;
    color: #6c757d;
    display: flex;
    flex-direction: column;
    padding: 3rem 1.5rem;
    text-align: center;
}

.sponsor-empty-state > .pi {
    color: #426cc6;
    font-size: 1.9rem;
    margin-bottom: 0.85rem;
}

.sponsor-empty-state h2 {
    color: #343a40;
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0 0 0.35rem;
}

.sponsor-empty-state p {
    margin: 0 0 1rem;
}

@media (max-width: 767.98px) {
    .sponsor-toolbar {
        align-items: stretch;
        flex-direction: column;
    }

    .sponsor-search .form-control {
        max-width: none;
        width: 100%;
    }

    .add-sponsor-button {
        align-self: flex-start;
    }
}
</style>
